import React, { useState, useEffect, useMemo } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { viewState } from "../../Redux/Actions/settingsActions";
import toast from "react-hot-toast";
import "./editsState.css";

const EditState = () => {
  const { id } = useParams();
  const idMemo = useMemo(() => id, [id]);

  const { stateData, loading = false } = useSelector((state) => state.states || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [statePayload, setStatePayload] = useState({
    id: idMemo,
    state_name: "",
    country_name: "",
  });

  const [errors, setErrors] = useState({});
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (idMemo) {
      dispatch(viewState(idMemo)).finally(() => setPageLoading(false));
    } else {
      setPageLoading(false);
    }
  }, [dispatch, idMemo]);

  useEffect(() => {
    if (stateData?.data?.length > 0) {
      const stateItem = stateData.data[0];
      setStatePayload({
        id: stateItem._id,
        state_name: stateItem.state_name || "",
        country_name: stateItem.country?.country_name || "",
      });
      setPageLoading(false);
    }
  }, [stateData]);

  const validate = () => {
    const newErrors = {};
    if (!statePayload.state_name.trim()) {
      newErrors.state_name = "State name is required";
    } else if (statePayload.state_name.trim().length < 2) {
      newErrors.state_name = "State name must be at least 2 characters";
    } else if (statePayload.state_name.trim().length > 100) {
      newErrors.state_name = "State name must be under 100 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please fix the errors before submitting");
      return;
    }
    try {
      const response = await dispatch(viewState(statePayload));
      if (response?.statusCode === 200) {
        toast.success("State updated successfully!");
        navigate(-1);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update state");
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setStatePayload((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  if (pageLoading) {
    return (
      <MainLayout>
        <div className="est-loading">
          <div className="est-loading-spinner" />
          <p>Loading state data...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="est-wrapper">

        {/* Header */}
        <div className="est-header">
          <div className="est-header-left">
            <div className="est-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <div>
              <h1 className="est-title">Edit State</h1>
              <p className="est-subtitle">
                {statePayload.state_name ? `Editing — ${statePayload.state_name}` : "Update state details"}
              </p>
            </div>
          </div>
          <button onClick={() => navigate(-1)} className="est-view-btn" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Go Back
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="est-form" noValidate>

          {/* Section 01: Location Context */}
          <div className="est-section">
            <div className="est-section-header">
              <span className="est-section-badge">01</span>
              <h2 className="est-section-title">Location Context</h2>
            </div>
            <div className="est-grid est-grid-1">
              <div className="est-field">
                <label className="est-label">Country</label>
                <div className="est-readonly-wrap">
                  <span className="est-readonly-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </span>
                  <input
                    className="est-input est-input--readonly"
                    type="text"
                    value={statePayload.country_name}
                    readOnly
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 02: State Information */}
          <div className="est-section">
            <div className="est-section-header">
              <span className="est-section-badge">02</span>
              <h2 className="est-section-title">State Information</h2>
            </div>
            <div className="est-grid est-grid-1">
              <div className="est-field">
                <label className="est-label">
                  State Name <span className="est-req">*</span>
                </label>
                <input
                  className={`est-input ${errors.state_name ? "est-input--error" : ""}`}
                  type="text"
                  name="state_name"
                  value={statePayload.state_name}
                  onChange={handleChange}
                  placeholder="e.g. Maharashtra"
                  disabled={loading}
                  maxLength={100}
                />
                {errors.state_name && (
                  <span className="est-error-msg">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.state_name}
                  </span>
                )}
                <span className="est-char-count">{statePayload.state_name.length}/100</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="est-footer">
            <button
              type="button"
              className="est-btn est-btn--cancel"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="est-btn est-btn--submit"
              disabled={loading}
            >
              {loading ? (
                <><span className="est-spinner" />Saving...</>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Update State
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditState;